// src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Layout from './components/Layout';
import SplashScreen from './components/SplashScreen';

import Home from './pages/Home';
import LabsPage from './pages/LabsPage';
import Profile from './pages/Profile';
import Trade from './pages/Trade';
import DepositWithdraw from './pages/DepositWithdraw';
import Transactions from './pages/Transactions';
import GoldRequest from './pages/GoldRequest';
import EditProfile from './pages/EditProfile';
import Referral from './pages/Referral';
import Security from './pages/Security';
import BankAccountsPage from './pages/BankAccountsPage';
import About from './pages/About';
import WalletRequests from './pages/WalletRequests';
import Tutorial from './pages/Tutorial';
import Faq from './pages/FAQ';
import Contact from './pages/Contact';
import PriceChart from './pages/PriceChart';
import VersionInfo from './pages/VersionInfo';

import AdminPanel from './pages/AdminPanel';
import AdminGrades from './pages/AdminGrades';
import Prices from './pages/Prices';
import AdminGoldRequests from './pages/AdminGoldRequests';
import AdminWalletRequests from './pages/AdminWalletRequests';
import AdminUserBalances from './pages/AdminUserBalances';
import AdminGoldTransactions from './pages/AdminGoldTransactions';
import AdminChargeAccount from './pages/AdminChargeAccount';
import AdminPriceSettings from './pages/AdminPriceSettings';
import AdminChatPanel from './pages/AdminChatPanel';

import Login from './pages/Login';
import Register from './pages/Register';
import ChatPage from './pages/ChatPage';

// PWA
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { toast } from 'react-toastify';

// تشخیص مرورگر
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [currentCacheName, setCurrentCacheName] = useState(null); // درست شد!

  // پایان اسپلش
  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  // آماده‌سازی اپ
  useEffect(() => {
    if (!showSplash) {
      const prepare = async () => {
        if (document.readyState !== 'complete') {
          await new Promise((r) => window.addEventListener('load', r, { once: true }));
        }
        if ('fonts' in document) await document.fonts.ready;
        await new Promise((r) => setTimeout(r, 1000));
        setIsReady(true);
      };
      prepare();
    }
  }, [showSplash]);

  // خواندن CACHE_NAME از service-worker.js
  useEffect(() => {
    if (isReady) {
      fetch('/service-worker.js?t=' + Date.now())
        .then((r) => r.text())
        .then((text) => {
          const match = text.match(/const CACHE_NAME = "(pwa-eg-cache-v[^"]+)"/);
          if (match) {
            setCurrentCacheName(match[1]); // درست شد!
          }
        })
        .catch(() => {});
    }
  }, [isReady]);

  // مدیریت بنر بروزرسانی
  useEffect(() => {
    if (isReady && currentCacheName) {
      const lastSeenCache = localStorage.getItem('pwa-last-seen-cache');
      const updateInProgress = localStorage.getItem('pwa-update-in-progress') === 'true';

      if ((isSafari || isIOS) && !updateInProgress && lastSeenCache !== currentCacheName) {
        setShowUpdateBanner(true);

        const interval = setInterval(() => {
          serviceWorkerRegistration.forceUpdate();
        }, 2 * 60 * 1000);

        return () => clearInterval(interval);
      } else {
        setShowUpdateBanner(false);
      }
    }
  }, [isReady, currentCacheName]);

  // تابع بروزرسانی دستی
  const handleUpdate = () => {
    localStorage.setItem('pwa-update-in-progress', 'true');

    toast.info('در حال دریافت بروزرسانی...', {
      toastId: 'updating',
      autoClose: 2000,
    });

    serviceWorkerRegistration.forceUpdate();

    setTimeout(() => {
      toast.success('صفحه در حال بارگذاری مجدد است...', {
        toastId: 'reloading',
        autoClose: 1500,
      });
      window.location.reload();
    }, 1500);
  };

  // بعد از لود کامل: ذخیره نسخه جدید
  useEffect(() => {
    const handleLoad = () => {
      if (currentCacheName) {
        localStorage.setItem('pwa-last-seen-cache', currentCacheName);
        localStorage.removeItem('pwa-update-in-progress');
      }
    };
    window.addEventListener('load', handleLoad);
    return () => window.removeEventListener('load', handleLoad);
  }, [currentCacheName]);

  return (
    <>
      {/* اسپلش اسکرین */}
      <AnimatePresence>
        {showSplash && (
          <SplashScreen
            key="splash"
            onComplete={handleSplashComplete}
            minDuration={3000}
          />
        )}
      </AnimatePresence>

      {/* اپ اصلی */}
      {isReady && !showSplash && (
        <Router>
          {/* بنر بروزرسانی زیبا */}
          <AnimatePresence>
            {showUpdateBanner && (
              <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                style={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
                  zIndex: 9998,
                  fontFamily: 'Vazirmatn, Tahoma, sans-serif',
                  borderRadius: '16px 16px 0 0',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {/* آیکون + متن */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    <motion.svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <path d="M21 2v6h-6" />
                      <path d="M3 12a9 9 0 0 1 15-6.7" />
                      <path d="M3 22v-6h6" />
                      <path d="M21 12a9 9 0 0 1-15 6.7" />
                    </motion.svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
                      بروزرسانی جدید موجود است
                    </div>
                    <div style={{ fontSize: '13px', opacity: 0.9 }}>
                      برای تجربه بهتر، صفحه را بروزرسانی کنید
                    </div>
                  </div>
                </div>

                {/* دکمه‌ها */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      localStorage.setItem('pwa-last-seen-cache', currentCacheName);
                      setShowUpdateBanner(false);
                    }}
                    style={{
                      background: 'transparent',
                      color: 'rgba(255, 255, 255, 0.8)',
                      border: 'none',
                      fontSize: '14px',
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: 8,
                    }}
                  >
                    بعداً
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={handleUpdate}
                    style={{
                      background: 'rgba(255, 255, 255, 0.25)',
                      color: 'white',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: 'pointer',
                      padding: '8px 16px',
                      borderRadius: 12,
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    بروزرسانی
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* روت‌ها */}
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="price-chart" element={<PriceChart />} />
                <Route path="profile_page" element={<Profile />} />
                <Route path="trade_page" element={<Trade />} />
                <Route path="deposit-withdraw_page" element={<DepositWithdraw />} />
                <Route path="transactions_page" element={<Transactions />} />
                <Route path="gold-request_page" element={<GoldRequest />} />
                <Route path="edit-profile_page" element={<EditProfile />} />
                <Route path="referral_page" element={<Referral />} />
                <Route path="security_page" element={<Security />} />
                <Route path="bank-accounts_page" element={<BankAccountsPage />} />
                <Route path="about_page" element={<About />} />
                <Route path="wallet-requests_page" element={<WalletRequests />} />
                <Route path="Contact" element={<Contact />} />
                <Route path="version-info" element={<VersionInfo />} />

                <Route path="AdminPanel_page" element={<AdminPanel />} />
                <Route path="admin-grades_page" element={<AdminGrades />} />
                <Route path="prices_page" element={<Prices />} />
                <Route path="admin-gold-requests_page" element={<AdminGoldRequests />} />
                <Route path="AdminWalletRequests_page" element={<AdminWalletRequests />} />
                <Route path="AdminUserBalances_page" element={<AdminUserBalances />} />
                <Route path="AdminGoldTransactions_page" element={<AdminGoldTransactions />} />
                <Route path="AdminChargeAccount_page" element={<AdminChargeAccount />} />
                <Route path="AdminPriceSettings_page" element={<AdminPriceSettings />} />

                <Route path="Login_page" element={<Login />} />
                <Route path="Register_page" element={<Register />} />
              </Route>

              <Route path="/chat_page" element={<ChatPage />} />
              <Route path="/admin-chat_page" element={<AdminChatPanel />} />
              <Route path="/tutorial" element={<Tutorial />} />
              <Route path="/FAQ" element={<Faq />} />
              <Route path="labs-page" element={<LabsPage />} />
            </Routes>
          </AnimatePresence>
        </Router>
      )}
    </>
  );
}

export default App;